import React from 'react';
import { X, Video, Upload, Save, CheckCircle } from 'lucide-react';
import Button from './Button';
import ProgressBar from './ProgressBar';

interface VideoProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: 'combining' | 'uploading' | 'saving' | 'completed';
  progress: number;
  message: string;
  canClose?: boolean;
}

const VideoProgressModal: React.FC<VideoProgressModalProps> = ({
  isOpen,
  onClose,
  currentStep,
  progress,
  message,
  canClose = false
}) => {
  if (!isOpen) return null;

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'combining':
        return <Video className="h-5 w-5 text-blue-500" />;
      case 'uploading':
        return <Upload className="h-5 w-5 text-yellow-500" />;
      case 'saving':
        return <Save className="h-5 w-5 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <Video className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStepLabel = (step: string) => {
    switch (step) {
      case 'combining':
        return 'Rendering Videos';
      case 'uploading':
        return 'Uploading Video';
      case 'saving':
        return 'Saving Session';
      case 'completed':
        return 'Completed';
      default:
        return 'Processing';
    }
  };

  const getProgressColor = (step: string) => {
    switch (step) {
      case 'combining':
        return 'primary';
      case 'uploading':
        return 'warning';
      case 'saving':
        return 'success';
      case 'completed':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="etr-modal-backdrop" />
      <div className="etr-modal-panel relative p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getStepIcon(currentStep)}
            <h3 className="text-lg font-semibold text-gray-900">
              {getStepLabel(currentStep)}
            </h3>
          </div>
          {canClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mb-4">
          <ProgressBar
            progress={progress}
            size="lg"
            color={getProgressColor(currentStep) as any}
            showPercentage={true}
          />
        </div>

        <p className="text-sm text-gray-600 text-center">
          {message}
        </p>

        {currentStep === 'completed' && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="primary"
              size="md"
              onClick={onClose}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoProgressModal;
